import { Layer, ObjectCategory, ZIndexes } from "@common/constants";
import { Flood, type StormData, type TornadoData, Tornadoes } from "@common/definitions/storm";
import { FloodField } from "@common/utils/flood";
import { LayerContainer } from "@common/utils/layer";
import { type SkyName } from "@common/utils/mapWeather";
import { Numeric } from "@common/utils/math";
import { Vec, type Vector } from "@common/utils/vector";
import { Container, Graphics, Sprite } from "pixi.js";
import { Game } from "../game";
import { PIXI_SCALE } from "../utils/constants";
import { TrenchioSprite, toPixiCoords } from "../utils/pixi";
import { t } from "../utils/translations/phrases";
import { WarLook } from "../war/warLook";
import { WarSky } from "../war/warSky";
import { CameraManager } from "./cameraManager";
import { FloodWater } from "./floodWater";
import { Lighting } from "./lighting";
import { sunDirection } from "./lightingLogic";
import { MapManager } from "./mapManager";
import { ParticleManager } from "./particleManager";
import { type GameSound, SoundManager } from "./soundManager";
import { TornadoFunnel } from "./tornadoFunnel";
import { UIManager } from "./uiManager";

/*
    The thunderstorm's weather on the page (0.98.4, definitions/storm.ts; the server decides it: server/src/storm/
    stormManager.ts). The tornadoes: each a funnel of turning cloud over a dark ground (0.98.5: drawn by the graphics
    card where the shaders are, tornadoFunnel.ts; the sprite funnel kept for the Low look), its debris whirling round it
    (planks, tiles, leaves, each with its shadow on the ground), lightning inside it now and then, its roar; the flood:
    the water over the flooded cells (utils/flood.ts, reckoned here from the map's rivers as the server reckons it; 0.98.5:
    drawn by the graphics card from the field, floodWater.ts, the river's own water swollen first and the flood growing
    out of it; the plain water kept for the Low look), things floating off with it, and the museum's dinosaur's bones
    carried off when it comes down; the notices, as a tornado forms and as the river comes over its banks.
*/

/** the storm's things over the world: the funnels over everything on the ground (the roofs and the trees too), the water over the floors */
const TORNADO_ZINDEX = ZIndexes.ObstaclesLayer5 + 0.5;
/** the water and what floats on it: in the ground floor's own container, over its decals and under what stands in it (the
 * players wade in it with their water overlay, the terrain's floor being water there; a floor upstairs stays dry) */
const WATER_ZINDEX = ZIndexes.Decals + 0.5;
const groundContainer = (): Container => CameraManager.layerContainers[LayerContainer.Ground];
/** the debris a funnel whirls round it: how many pieces, and the pictures they're drawn with */
const DEBRIS_COUNT = 26;
const DEBRIS_FRAMES = ["debris_plank", "debris_tile_1", "debris_tile_2", "debris_paper", "leaf_particle_1", "leaf_particle_2", "debris_branch"];
/** the plain water's colour (the Low look: a silty murk) and how much of the ground shows through it; the foam's */
const WATER_COLOR = 0x3b4a43;
const WATER_ALPHA = 0.66;
const FOAM_COLOR = 0xdde9ee;
/** the floating things: how many at most, the pictures */
const MAX_FLOATING = 28;
const FLOAT_FRAMES = ["flood_debris_1", "flood_debris_2", "flood_debris_3", "flood_debris_4"];
const BONE_FRAMES = ["dino_bone_1", "dino_bone_2", "dino_bone_3"];

interface TornadoView {
    readonly data: TornadoData
    /** where it's drawn, smoothed towards where the server says */
    position: Vector
    readonly container: Container
    readonly shadow: Sprite
    readonly spirals: Sprite[]
    readonly core: Sprite
    readonly dust: Sprite[]
    readonly debris: { sprite: Sprite, shade: Sprite, angle: number, radius: number, rate: number, lift: number, spin: number }[]
    readonly roar: GameSound
    /** the funnel on the graphics card, where the shaders are drawn (else the sprites above) */
    funnel?: TornadoFunnel
    /** the lightning inside it: how bright now, and when it next strikes */
    flash: number
    nextFlash: number
    lastAt: number
    life: number
}

interface Floating {
    readonly sprite: Sprite
    position: Vector
    readonly born: number
    readonly life: number
    readonly spin: number
    readonly bob: number
}

class StormViewClass {
    private _storm = false;
    private _field?: FloodField;
    private _level = 0;
    private _levelShown = 0;
    private _levelFrameAt = 0;
    private readonly _tornadoes = new Map<number, TornadoView>();
    private _layer?: Container;
    private _water?: Graphics;
    private _foam?: Graphics;
    /** the water on the graphics card, where the shaders are drawn (else the plain water above) */
    private _sheet?: FloodWater;
    /** the storm's own clock for its shaders (seconds since the view began: small numbers, exact on the card) */
    private _startedAt = 0;
    private _waterKey = "";
    private readonly _floating: Floating[] = [];
    private _lastFloat = 0;
    private _lastRipple = 0;
    private _tornadoSeen = false;
    private _floodSeen = false;
    private _bonesDone = false;
    private _rush?: GameSound;
    private readonly _lastDebrisBurst = new Map<number, number>();

    get storm(): boolean { return this._storm; }
    get field(): FloodField | undefined { return this._field; }
    get level(): number { return this._level; }

    /** the map packet came, under this sky */
    begin(sky: SkyName): void {
        this.reset();
        this._storm = sky === "storm";
        this._startedAt = Date.now();
    }

    /** the map's terrain is built: the flood's field from its rivers, as the server reckons it */
    mapReady(): void {
        if (!this._storm) return;
        const terrain = MapManager.terrain;
        this._field = FloodField.of(terrain, MapManager.width, MapManager.height);
        if (this._field) terrain.flood = { field: this._field, level: 0 };
    }

    /** the server's word: the tornadoes crossing now and the water's spread */
    updateFrom(storm: StormData): void {
        if (!this._storm) return;
        const now = Date.now();
        const seen = new Set<number>();
        for (const data of storm.tornadoes) {
            seen.add(data.id);
            let view = this._tornadoes.get(data.id);
            if (!view) {
                view = this._makeTornado(data);
                this._tornadoes.set(data.id, view);
                if (!this._tornadoSeen) {
                    this._tornadoSeen = true;
                    this._notice(t("A tornado has touched down in the north"));
                }
            }
            (view as { data: TornadoData }).data = data;
            view.lastAt = now;
            view.life = data.life;
        }
        for (const [id, view] of this._tornadoes) {
            if (seen.has(id)) continue;
            this._destroyTornado(view);
            this._tornadoes.delete(id);
        }
        if (storm.flood !== this._level) {
            this._level = storm.flood;
            const terrain = MapManager.terrain;
            if (terrain.flood) terrain.flood.level = storm.flood;
            if (storm.flood > 0 && !this._floodSeen) {
                this._floodSeen = true;
                this._notice(t("The river is over its banks: the water is rising"));
            }
        }
    }

    private _notice(words: string): void {
        if (!Game.gameStarted || Game.gameOver) return;
        const { inventoryMsg } = UIManager.ui;
        inventoryMsg.text(words).fadeOut(0).fadeIn(250);
        clearTimeout(Game.inventoryMsgTimeout);
        Game.inventoryMsgTimeout = window.setTimeout(() => inventoryMsg.fadeOut(250), 6000);
    }

    private _container(): Container {
        if (this._layer) return this._layer;
        const layer = new Container();
        layer.zIndex = TORNADO_ZINDEX;
        layer.eventMode = "none";
        layer.sortableChildren = true;
        CameraManager.addObject(layer);
        this._layer = layer;
        return layer;
    }

    // ---------------- the tornadoes ----------------

    private _makeTornado(data: TornadoData): TornadoView {
        const container = new Container();
        container.sortableChildren = true;
        this._container().addChild(container);
        const shadow = new TrenchioSprite("tornado_shadow");
        shadow.alpha = 0.45;
        shadow.zIndex = 0;
        container.addChild(shadow);
        const spirals: Sprite[] = [];
        for (let i = 0; i < 3; i++) {
            const spiral = new TrenchioSprite(`tornado_spiral_${i + 1}`);
            spiral.zIndex = 2 + i;
            spiral.alpha = 0.72 - i * 0.12;
            container.addChild(spiral);
            spirals.push(spiral);
        }
        const core = new TrenchioSprite("tornado_core");
        core.zIndex = 6;
        core.alpha = 0.8;
        container.addChild(core);
        const dust: Sprite[] = [];
        for (let i = 0; i < 10; i++) {
            const puff = new TrenchioSprite(i % 2 === 0 ? "dust_puff_1" : "dust_puff_2");
            puff.zIndex = 1;
            puff.tint = 0x8a7a66;
            container.addChild(puff);
            dust.push(puff);
        }
        const debris = Array.from({ length: DEBRIS_COUNT }, (_, i) => {
            const frame = DEBRIS_FRAMES[i % DEBRIS_FRAMES.length];
            const sprite = new TrenchioSprite(frame);
            sprite.zIndex = 7 + (i % 3);
            // (its shadow on the ground under it: the piece is seen to be up in the air)
            const shade = new TrenchioSprite(frame);
            shade.tint = 0x000000;
            shade.alpha = 0.3;
            shade.zIndex = 1.5;
            container.addChild(shade, sprite);
            return {
                sprite,
                shade,
                angle: Math.random() * Math.PI * 2,
                radius: 5 + Math.random() * 26,
                rate: 2.2 + Math.random() * 2.8,
                lift: Math.random(),
                spin: (Math.random() - 0.5) * 12
            };
        });
        const roar = SoundManager.play("tornado_roar", { position: data.position, loop: true, dynamic: true, ambient: true, falloff: 0.5, maxRange: 170, layer: Layer.Ground });
        const now = Date.now();
        return { data, position: Vec.clone(data.position), container, shadow, spirals, core, dust, debris, roar, flash: 0, nextFlash: now + 1500 + Math.random() * 3000, lastAt: now, life: data.life };
    }

    /** the funnel drawn by the graphics card where the shaders are, the sprites where they aren't (the look can drop mid-match) */
    private _funnelLook(view: TornadoView): void {
        const shaders = WarLook.shaders;
        if (shaders && !view.funnel) {
            const funnel = new TornadoFunnel();
            funnel.mesh.zIndex = 5;
            view.container.addChild(funnel.mesh);
            view.funnel = funnel;
        } else if (!shaders && view.funnel) {
            view.funnel.destroy();
            view.funnel = undefined;
        }
        const sprites = view.funnel === undefined;
        view.shadow.visible = view.core.visible = sprites;
        for (const spiral of view.spirals) spiral.visible = sprites;
        for (const puff of view.dust) puff.visible = sprites;
    }

    private _destroyTornado(view: TornadoView): void {
        view.roar.stop();
        view.funnel?.destroy();
        view.funnel = undefined;
        view.container.destroy({ children: true });
    }

    private _updateTornadoes(delta: number, now: number): void {
        const t = now / 1000;
        for (const view of this._tornadoes.values()) {
            // (smoothed towards the server's spot: the funnel moves between its words)
            const k = 1 - Math.exp(-delta / 90);
            view.position = Vec.lerp(view.position, view.data.position, k);
            const at = toPixiCoords(view.position);
            view.container.position.set(at.x, at.y);
            view.roar.position = view.position;
            // forming and dying away: the first and last tenth of its life
            const life = view.life;
            const presence = Math.min(1, life / 0.08, (1 - life) / 0.1);
            const size = presence * (1 + 0.06 * Math.sin(t * 1.7));
            this._funnelLook(view);
            if (view.funnel) {
                // lightning inside the cloud now and then: a flash that dies away in a tenth of a second
                if (now >= view.nextFlash) {
                    view.flash = 0.6 + Math.random() * 0.5;
                    view.nextFlash = now + 1200 + Math.random() * 5000;
                }
                view.flash *= Math.exp(-delta / 90);
                view.funnel.update((now - this._startedAt) / 1000, presence, view.flash);
            }
            view.shadow.scale.set((Tornadoes.pull * 1.6 * PIXI_SCALE) / 256 * size);
            view.shadow.alpha = 0.42 * presence;
            view.spirals.forEach((spiral, i) => {
                spiral.rotation = -t * (2.2 + i * 1.1) + i;
                const radius = (Tornadoes.radius * (2.6 - i * 0.55)) * PIXI_SCALE * size;
                spiral.scale.set(radius / 128);
                spiral.alpha = (0.72 - i * 0.12) * presence;
            });
            view.core.rotation = -t * 5;
            view.core.scale.set((Tornadoes.radius * 0.9 * PIXI_SCALE) / 128 * size * (1 + 0.1 * Math.sin(t * 5)));
            view.core.alpha = 0.8 * presence;
            view.dust.forEach((puff, i) => {
                const angle = -t * 1.6 + (i / view.dust.length) * Math.PI * 2;
                const radius = (Tornadoes.radius * 1.4 + 6 * Math.sin(t * 1.3 + i)) * PIXI_SCALE * size;
                puff.position.set(Math.cos(angle) * radius, Math.sin(angle) * radius);
                puff.rotation = t * 0.8 + i;
                puff.scale.set((9 * PIXI_SCALE) / 160 * size);
                puff.alpha = 0.5 * presence;
            });
            for (const piece of view.debris) {
                piece.angle -= piece.rate * delta / 1000;
                // (in and out as it goes round, higher the nearer the funnel: drawn bigger up there, over the rest, its
                // shadow left on the ground under it)
                const wobble = Math.sin(t * 1.1 + piece.spin) * 3;
                const radius = (piece.radius + wobble) * size;
                const height = piece.lift * (1 - radius / 40);
                const gx = Math.cos(piece.angle) * radius * PIXI_SCALE, gy = Math.sin(piece.angle) * radius * PIXI_SCALE;
                piece.sprite.position.set(gx + height * 1.2 * PIXI_SCALE, gy - height * 2.4 * PIXI_SCALE);
                piece.sprite.rotation += piece.spin * delta / 1000;
                piece.sprite.scale.set((0.9 + height * 0.8) * presence);
                piece.sprite.alpha = presence;
                piece.shade.position.set(gx, gy);
                piece.shade.rotation = piece.sprite.rotation;
                piece.shade.scale.set(0.85 * presence);
                piece.shade.alpha = 0.3 * presence * (1 - 0.5 * height);
            }
            // the ground torn up under it: dust and leaves thrown out
            if (presence > 0.5 && Math.random() < delta / 60) {
                const angle = Math.random() * Math.PI * 2;
                ParticleManager.spawnParticle({
                    frames: Math.random() < 0.7 ? "smoke_particle" : ["leaf_particle_1", "leaf_particle_2", "leaf_particle_3"],
                    position: Vec.add(view.position, Vec.fromPolar(angle, Tornadoes.radius * 0.8)),
                    lifetime: 900 + Math.random() * 700,
                    zIndex: TORNADO_ZINDEX - 0.1,
                    layer: Layer.Ground,
                    scale: { start: 0.6, end: 1.8 },
                    alpha: { start: 0.45, end: 0 },
                    rotation: { start: angle, end: angle + 3 },
                    tint: 0x8a7a66,
                    speed: Vec.fromPolar(angle + 1.2, 14 + Math.random() * 10)
                });
            }
            // the roofs it crosses: a storm of grit and paper off a concrete roof it shakes, the torn ones' debris is the building's
            this._shakeRoofs(view, now);
            // the camera shakes as it nears
            const me = Game.activePlayer;
            if (me && presence > 0.3) {
                const distance = Vec.len(Vec.sub(me.position, view.position));
                if (distance < 70 && Math.random() < delta / 120) CameraManager.shake(120, Numeric.clamp((70 - distance) / 70, 0, 1) * 7);
            }
        }
    }

    /** a concrete roof under the funnel (the blocks', the station's): grit, gravel and papers fly off it, though it stays */
    private _shakeRoofs(view: TornadoView, now: number): void {
        for (const building of Game.objects.getCategory(ObjectCategory.Building)) {
            const definition = building.definition;
            if (!definition.ceilingImages?.length || definition.openAir || !building.ceilingHitbox) continue;
            const distance = Vec.len(Vec.sub(building.position, view.position));
            if (distance > Tornadoes.pull + 60) continue;
            const rect = building.ceilingHitbox.toRectangle();
            const near = Vec(Numeric.clamp(view.position.x, rect.min.x, rect.max.x), Numeric.clamp(view.position.y, rect.min.y, rect.max.y));
            if (Vec.len(Vec.sub(near, view.position)) > Tornadoes.radius + 4) continue;
            const last = this._lastDebrisBurst.get(building.id) ?? 0;
            if (now - last < 160) continue;
            this._lastDebrisBurst.set(building.id, now);
            const spot = building.ceilingHitbox.randomPoint();
            ParticleManager.spawnParticles(3, () => ({
                frames: Math.random() < 0.5 ? "debris_paper" : "stone_particle_1",
                position: spot,
                lifetime: 1200 + Math.random() * 600,
                zIndex: TORNADO_ZINDEX + 0.2,
                layer: building.layer,
                scale: { start: 0.5 + Math.random() * 0.4, end: 0.2 },
                alpha: { start: 1, end: 0 },
                rotation: { start: Math.random() * 6, end: Math.random() * 12 },
                speed: Vec.fromPolar(Math.random() * Math.PI * 2, 12 + Math.random() * 16)
            }));
        }
    }

    // ---------------- the flood ----------------

    private _view(margin: number): { readonly min: Vector, readonly max: Vector } {
        const scale = CameraManager.container.scale.x || 1;
        const halfW = CameraManager.width / scale / 2 / PIXI_SCALE + margin;
        const halfH = CameraManager.height / scale / 2 / PIXI_SCALE + margin;
        const center = Vec.scale(CameraManager.position, 1 / PIXI_SCALE);
        return { min: Vec(center.x - halfW, center.y - halfH), max: Vec(center.x + halfW, center.y + halfH) };
    }

    /** the view and as much again round it: the sheet's quad (the camera may move on after this is reckoned, a watcher's leap; what's off the screen costs nothing) */
    private _wideView(): { readonly min: Vector, readonly max: Vector } {
        const view = this._view(0);
        const w = (view.max.x - view.min.x) * 0.7, h = (view.max.y - view.min.y) * 0.7;
        return { min: Vec(view.min.x - w, view.min.y - h), max: Vec(view.max.x + w, view.max.y + h) };
    }

    /** the water's spread as drawn: the server's, come to smoothly (it rises a tenth of a unit a tick), by the time
     * since the last frame (so a slow frame rate catches up all the same; a leap, a match joined late, in half a second) */
    private _shownLevel(now: number): number {
        const dt = Math.min(500, Math.max(0, now - this._levelFrameAt));
        this._levelFrameAt = now;
        this._levelShown = Numeric.lerp(this._levelShown, this._level, 1 - Math.exp(-dt / 180));
        if (Math.abs(this._level - this._levelShown) < 0.02) this._levelShown = this._level;
        return this._levelShown;
    }

    private _updateFlood(delta: number, now: number): void {
        const field = this._field;
        if (!field || this._level <= 0) {
            if (this._water) this._water.visible = false;
            if (this._foam) this._foam.visible = false;
            if (this._sheet) this._sheet.mesh.visible = false;
            return;
        }
        const level = this._shownLevel(now);
        const view = this._view(16);
        if (WarLook.shaders) {
            // the water on the graphics card: the quad over the screen, the field in its texture
            if (!this._sheet) {
                this._sheet = new FloodWater(field, WarLook.weather.wind);
                this._sheet.mesh.zIndex = WATER_ZINDEX;
                groundContainer().addChild(this._sheet.mesh);
            }
            this._sheet.mesh.visible = true;
            // (0.98.6: lit from the sun's side, flashing with the lightning)
            const toward = sunDirection(Lighting.sun);
            this._sheet.update(this._wideView(), level, (now - this._startedAt) / 1000, { x: -toward.x, y: -toward.y, flash: WarSky.lightning()?.alpha ?? 0 });
            if (this._water) this._water.visible = false;
            if (this._foam) this._foam.visible = false;
            this._waterKey = "";
        } else {
            if (this._sheet) {
                this._sheet.destroy();
                this._sheet = undefined;
            }
            const cell = field.cell;
            const c0 = Math.max(0, Math.floor(view.min.x / cell)), c1 = Math.min(field.columns - 1, Math.floor(view.max.x / cell));
            const r0 = Math.max(0, Math.floor(view.min.y / cell)), r1 = Math.min(field.rows - 1, Math.floor(view.max.y / cell));
            const key = `${c0},${c1},${r0},${r1},${Math.floor(level * 2)}`;
            if (key !== this._waterKey) {
                this._waterKey = key;
                this._drawWater(field, level, c0, c1, r0, r1);
            }
            // the water's lap: the whole sheet breathes a little
            if (this._water) this._water.alpha = WATER_ALPHA * (0.96 + 0.04 * Math.sin(now / 900));
            if (this._foam) this._foam.alpha = 0.7 + 0.3 * Math.sin(now / 500);
        }

        // rain on the water, rings here and there, and the current's streaks (the plain water's: the sheet draws its own)
        if (!this._sheet && now - this._lastRipple > 90) {
            this._lastRipple = now;
            for (let k = 0; k < 5; k++) {
                const spot = Vec(view.min.x + Math.random() * (view.max.x - view.min.x), view.min.y + Math.random() * (view.max.y - view.min.y));
                if (!field.floodedAt(spot, level)) continue;
                const streak = Math.random() < 0.4;
                const flow = field.flowAt(spot);
                ParticleManager.spawnParticle({
                    frames: streak ? "flood_streak" : "ripple_particle",
                    position: spot,
                    lifetime: streak ? 1800 : 1100,
                    zIndex: WATER_ZINDEX + 0.1,
                    layer: Layer.Ground,
                    scale: streak ? { start: 0.5, end: 1.1 } : { start: 0.08, end: 0.5 },
                    alpha: streak ? { start: 0.42, end: 0 } : { start: 0.5, end: 0 },
                    rotation: streak ? Math.atan2(flow.y, flow.x) : 0,
                    tint: streak ? 0xdfecf2 : 0xbcd6e4,
                    speed: streak ? Vec.scale(flow, 6) : Vec(0, 0)
                });
            }
        }
        this._updateFloating(field, level, view, now, delta);
        this._wakes(field, level, view, now);
        this._bones(field, level);
        // the water's rush, heard near its edge
        const me = Game.activePlayer;
        if (me) {
            const depth = field.depthAt(me.position, level);
            const near = depth > 0 ? 0.55 + Math.min(0.45, depth / 80) : Numeric.clamp(1 - (field.distanceAt(me.position) - level) / 40, 0, 0.5);
            if (near > 0.02) {
                this._rush ??= SoundManager.play("flood_rush", { loop: true, ambient: true, dynamic: true, layer: Layer.Ground, falloff: 0, maxRange: 1, volume: near });
                this._rush.volume = near;
            } else if (this._rush) {
                this._rush.stop();
                this._rush = undefined;
            }
        }
    }

    /**
     * the water over the flooded cells on screen, its edge scalloped where it meets dry ground (a lobe of water past
     * each cell's side, no two alike, so the grid's straight lines don't show), and the foam along that edge: a broken
     * line of it with flecks either side. Drawn again when the screen or the level moves
     */
    private _drawWater(field: FloodField, level: number, c0: number, c1: number, r0: number, r1: number): void {
        if (!this._water) {
            this._water = new Graphics();
            this._water.zIndex = WATER_ZINDEX;
            this._foam = new Graphics();
            this._foam.zIndex = WATER_ZINDEX + 0.05;
            groundContainer().addChild(this._water, this._foam);
        }
        const water = this._water, foam = this._foam;
        if (!foam) return;
        water.visible = foam.visible = true;
        water.clear();
        foam.clear();
        const cell = field.cell;
        const px = cell * PIXI_SCALE;
        const hash = (a: number, b: number, salt = 0): number => {
            const n = Math.sin(a * 127.1 + b * 311.7 + salt * 74.7) * 43758.5453;
            return n - Math.floor(n);
        };
        const edges: [number, number, number, number, number, number][] = [];
        for (let row = r0; row <= r1; row++) {
            let run = -1;
            for (let column = c0; column <= c1 + 1; column++) {
                const flooded = column <= c1 && field.cellFlooded(column, row, level);
                if (flooded && run < 0) run = column;
                if (!flooded && run >= 0) {
                    water.rect(run * px, row * px, (column - run) * px, px);
                    run = -1;
                }
                if (!flooded) continue;
                // a cell's side that faces dry ground (not the river's own bank: the water came from there)
                const x = column * px, y = row * px;
                if (!field.cellFlooded(column, row - 1, level) && field.cellCap(column, row - 1) >= 0) edges.push([x, y, x + px, y, 0, -1]);
                if (!field.cellFlooded(column, row + 1, level) && field.cellCap(column, row + 1) >= 0) edges.push([x, y + px, x + px, y + px, 0, 1]);
                if (!field.cellFlooded(column - 1, row, level) && field.cellCap(column - 1, row) >= 0) edges.push([x, y, x, y + px, -1, 0]);
                if (!field.cellFlooded(column + 1, row, level) && field.cellCap(column + 1, row) >= 0) edges.push([x + px, y, x + px, y + px, 1, 0]);
            }
        }
        // the lobes: the water bulging past each dry-facing side, by the side's own measure
        for (const [x0, y0, x1, y1, nx, ny] of edges) {
            const mx = (x0 + x1) / 2, my = (y0 + y1) / 2;
            const h = hash(mx, my);
            const out = (0.9 + 1.4 * h) * PIXI_SCALE;
            const along = px * (0.42 + 0.14 * hash(mx, my, 1));
            water.ellipse(mx + nx * out * 0.35, my + ny * out * 0.35, nx === 0 ? along : out, nx === 0 ? out : along);
        }
        water.fill({ color: WATER_COLOR, alpha: 1 });
        // the foam: a broken line along the lobed edge, and flecks of it on the water and the ground just past it
        for (const [x0, y0, x1, y1, nx, ny] of edges) {
            const mx = (x0 + x1) / 2, my = (y0 + y1) / 2;
            const h = hash(mx, my);
            const out = (0.9 + 1.4 * h) * PIXI_SCALE * 0.7;
            const dashes = 2 + Math.floor(hash(mx, my, 2) * 2);
            for (let d = 0; d < dashes; d++) {
                const a = (d + 0.15 + 0.1 * hash(mx, my, 3 + d)) / dashes, b = (d + 0.85) / dashes;
                const ax = Numeric.lerp(x0, x1, a) + nx * out, ay = Numeric.lerp(y0, y1, a) + ny * out;
                const bx = Numeric.lerp(x0, x1, b) + nx * out, by = Numeric.lerp(y0, y1, b) + ny * out;
                const w = (0.32 + 0.2 * hash(mx, my, 5 + d)) * PIXI_SCALE;
                foam.roundRect(Math.min(ax, bx) - w / 2, Math.min(ay, by) - w / 2, Math.abs(bx - ax) + w, Math.abs(by - ay) + w, w / 2);
            }
            for (let k = 0; k < 3; k++) {
                const t = hash(mx, my, 10 + k);
                const side = hash(mx, my, 20 + k) < 0.5 ? -1 : 1;
                const fx = Numeric.lerp(x0, x1, t) + nx * (out + side * (0.3 + 0.9 * hash(mx, my, 30 + k)) * PIXI_SCALE);
                const fy = Numeric.lerp(y0, y1, t) + ny * (out + side * (0.3 + 0.9 * hash(mx, my, 40 + k)) * PIXI_SCALE);
                foam.circle(fx, fy, (0.12 + 0.2 * hash(mx, my, 50 + k)) * PIXI_SCALE);
            }
        }
        foam.fill({ color: FOAM_COLOR, alpha: 0.8 });
    }

    private _lastWake = 0;

    /** the water round whatever stands in it: ripples off the trees' trunks, the cars, the crates, now and then each */
    private _wakes(field: FloodField, level: number, view: { readonly min: Vector, readonly max: Vector }, now: number): void {
        if (now - this._lastWake < 140) return;
        this._lastWake = now;
        let spawned = 0;
        for (const obstacle of Game.objects.getCategory(ObjectCategory.Obstacle)) {
            if (spawned >= 4) break;
            if (obstacle.dead || obstacle.layer !== Layer.Ground || obstacle.definition.noCollisions || obstacle.definition.isDoor) continue;
            const { x, y } = obstacle.position;
            if (x < view.min.x || x > view.max.x || y < view.min.y || y > view.max.y) continue;
            if (Math.random() > 0.22 || field.depthAt(obstacle.position, level) <= 1.5) continue;
            const rect = obstacle.hitbox.toRectangle();
            const reach = Math.max(rect.max.x - rect.min.x, rect.max.y - rect.min.y) / 2 + 0.6;
            const spot = Vec.add(obstacle.position, Vec.fromPolar(Math.random() * Math.PI * 2, reach));
            spawned++;
            ParticleManager.spawnParticle({
                frames: "ripple_particle",
                position: spot,
                lifetime: 1300,
                zIndex: WATER_ZINDEX + 0.12,
                layer: Layer.Ground,
                scale: { start: 0.12, end: 0.55 },
                alpha: { start: 0.55, end: 0 },
                tint: 0xcfe0e8,
                speed: Vec(0, 0)
            });
        }
    }

    /** things floating off with the water: planks, a tyre, bottles, branches, drifting with the current from the edge */
    private _updateFloating(field: FloodField, level: number, view: { readonly min: Vector, readonly max: Vector }, now: number, delta: number): void {
        if (now - this._lastFloat > 900 && this._floating.length < MAX_FLOATING) {
            this._lastFloat = now;
            for (let tries = 0; tries < 6; tries++) {
                const spot = Vec(view.min.x + Math.random() * (view.max.x - view.min.x), view.min.y + Math.random() * (view.max.y - view.min.y));
                const depth = field.depthAt(spot, level);
                if (depth <= 2 || depth > 40 || field.capAt(spot) < 0) continue;
                const sprite = new TrenchioSprite(FLOAT_FRAMES[Math.floor(Math.random() * FLOAT_FRAMES.length)]);
                sprite.zIndex = WATER_ZINDEX + 0.2;
                sprite.rotation = Math.random() * 6.28;
                groundContainer().addChild(sprite);
                this._floating.push({ sprite, position: spot, born: now, life: 14_000 + Math.random() * 10_000, spin: (Math.random() - 0.5) * 0.4, bob: Math.random() * 6.28 });
                break;
            }
        }
        for (const thing of [...this._floating]) {
            const age = now - thing.born;
            if (age > thing.life || !field.floodedAt(thing.position, level)) {
                thing.sprite.destroy();
                this._floating.splice(this._floating.indexOf(thing), 1);
                continue;
            }
            const flow = field.flowAt(thing.position);
            const speed = Flood.drift.loot * 0.9;
            thing.position = Vec.add(thing.position, Vec.scale(flow, speed * delta / 1000));
            const at = toPixiCoords(thing.position);
            const bob = Math.sin(now / 700 + thing.bob) * 0.25 * PIXI_SCALE;
            thing.sprite.position.set(at.x, at.y + bob);
            thing.sprite.rotation += thing.spin * delta / 1000;
            thing.sprite.alpha = Math.min(1, age / 800, (thing.life - age) / 1500);
        }
    }

    /** the museum's middle dinosaur come down in the water (the server's word, its obstacle dead): its bones float off */
    private _bones(field: FloodField, level: number): void {
        if (this._bonesDone) return;
        for (const obstacle of Game.objects.getCategory(ObjectCategory.Obstacle)) {
            if (obstacle.definition.idString !== Flood.dinosaur || !obstacle.dead) continue;
            if (!field.floodedAt(obstacle.position, level)) continue;
            this._bonesDone = true;
            const now = Date.now();
            SoundManager.play("dinosaur_shatter", { position: obstacle.position, falloff: 0.4, maxRange: 140, layer: Layer.Ground });
            for (let i = 0; i < 14; i++) {
                const sprite = new TrenchioSprite(BONE_FRAMES[i % BONE_FRAMES.length]);
                sprite.zIndex = WATER_ZINDEX + 0.25;
                sprite.rotation = Math.random() * 6.28;
                groundContainer().addChild(sprite);
                const position = Vec.add(obstacle.position, Vec.fromPolar(Math.random() * 6.28, Math.random() * 18));
                this._floating.push({ sprite, position, born: now, life: 60_000 + Math.random() * 30_000, spin: (Math.random() - 0.5) * 0.5, bob: Math.random() * 6.28 });
            }
            return;
        }
    }

    /** every frame */
    update(delta: number): void {
        if (!this._storm || !Game.gameStarted) return;
        const now = Date.now();
        this._updateTornadoes(delta, now);
        this._updateFlood(delta, now);
        this._updateLights(now);
    }

    // ---------------- the street lights (the owner: "street lights flicker") ----------------

    private readonly _lights = new Map<number, { readonly glow: Sprite, readonly bulb: Sprite, readonly seed: number, on: boolean, lastBuzz: number }>();

    /**
     * the street lights in the storm: lit against the dark sky, each lamp's light guttering and cutting out as the
     * storm plays with the power, a buzz heard when one near you flickers; a lamp shot down is dark
     */
    private _updateLights(now: number): void {
        const seen = new Set<number>();
        const t = now / 1000;
        for (const obstacle of Game.objects.getCategory(ObjectCategory.Obstacle)) {
            const lamp = obstacle.definition.lamp;
            if (!lamp) continue;
            seen.add(obstacle.id);
            let light = this._lights.get(obstacle.id);
            if (!light) {
                const glow = new TrenchioSprite("_glow_");
                glow.blendMode = "add";
                glow.tint = 0xffc27a;
                glow.zIndex = ZIndexes.ObstaclesLayer5 + 0.3;
                const bulb = new TrenchioSprite("_glow_");
                bulb.blendMode = "add";
                bulb.tint = 0xfff1c8;
                bulb.zIndex = ZIndexes.ObstaclesLayer5 + 0.31;
                CameraManager.getContainer(obstacle.layer).addChild(glow, bulb);
                const at = Vec.addAdjust(obstacle.position, lamp.offset, obstacle.orientation);
                const px = toPixiCoords(at);
                glow.position.set(px.x, px.y);
                bulb.position.set(px.x, px.y);
                glow.scale.set((lamp.radius * 0.75 * PIXI_SCALE) / 128);
                bulb.scale.set((2.2 * PIXI_SCALE) / 128);
                light = { glow, bulb, seed: obstacle.id * 0.731, on: true, lastBuzz: 0 };
                this._lights.set(obstacle.id, light);
            }
            if (obstacle.dead) {
                light.glow.visible = light.bulb.visible = false;
                continue;
            }
            // the flicker: a slow waver, and cut-outs when the storm's noise dips (each lamp its own)
            const s = light.seed;
            const waver = 0.75 + 0.25 * Math.sin(t * 7.3 + s) * Math.sin(t * 2.1 + s * 3);
            const noise = Math.sin(t * 1.7 + s) + Math.sin(t * 4.9 + s * 2.3) * 0.6 + Math.sin(t * 13.1 + s * 5.1) * 0.35;
            const on = noise > -0.55;
            const stutter = noise > -0.55 && noise < -0.35 ? (Math.sin(t * 60 + s) > 0 ? 1 : 0.15) : 1;
            const level = on ? waver * stutter : 0.04;
            light.glow.visible = light.bulb.visible = true;
            light.glow.alpha = 0.5 * level;
            light.bulb.alpha = 0.9 * level;
            if (on !== light.on) {
                light.on = on;
                const me = Game.activePlayer;
                if (me && now - light.lastBuzz > 900 && Vec.squaredLen(Vec.sub(me.position, obstacle.position)) < 45 * 45) {
                    light.lastBuzz = now;
                    SoundManager.play("lamp_buzz", { position: obstacle.position, falloff: 0.6, maxRange: 50, layer: obstacle.layer, variation: { pitch: 0.1 } });
                }
            }
        }
        for (const [id, light] of this._lights) {
            if (seen.has(id)) continue;
            light.glow.destroy();
            light.bulb.destroy();
            this._lights.delete(id);
        }
    }

    /** the tornadoes on screen (the lab and the tests) */
    get tornadoCount(): number { return this._tornadoes.size; }

    reset(): void {
        for (const view of this._tornadoes.values()) this._destroyTornado(view);
        this._tornadoes.clear();
        for (const thing of this._floating) thing.sprite.destroy();
        this._floating.length = 0;
        this._water?.destroy();
        this._foam?.destroy();
        this._water = this._foam = undefined;
        this._sheet?.destroy();
        this._sheet = undefined;
        this._waterKey = "";
        this._layer?.destroy({ children: true });
        this._layer = undefined;
        this._rush?.stop();
        this._rush = undefined;
        this._field = undefined;
        this._level = this._levelShown = 0;
        this._storm = false;
        this._tornadoSeen = this._floodSeen = this._bonesDone = false;
        this._lastDebrisBurst.clear();
        for (const light of this._lights.values()) { light.glow.destroy(); light.bulb.destroy(); }
        this._lights.clear();
        const terrain = MapManager.terrain as { flood?: unknown } | undefined;
        if (terrain) terrain.flood = undefined;
    }
}

export const StormView = new StormViewClass();
